import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { FleetGrid, useGridList } from '../../../../shared/sso-workspace/fleet-grid'
import { Notice, RollupTiles } from '../../../../shared/sso-workspace/fleet-page'
import { TableAgClient } from '../../../../shared/table-builder/table-ag-client'
import { ColumnFormat, ColumnText } from '../../../../shared/table-builder/table-columns'
import { IconDownload, IconList } from '../../../../shared/icon/icon'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { formatCount } from '../../../../shared/sso-workspace/format'
import { addDays, dayOf, dayWindow, zoneName } from '../../../../shared/date/local-time'
import { UNDER_COUNT_NOTE } from '../../org/api-keys/key-words'
import { downloadUsageCsv, getSysUsage } from './sys-api'

// Every organization's API usage (Task 16; ruling 10), or one's: content's
// rollup per key per UTC day over a range of the reader's days (GET
// /api-key-sys/usage: { items: [{ day, organization_id, organization,
// api_key_id, key_name, requests, outcomes: { 2xx, 4xx, 429, 5xx } }],
// truncated }), and its CSV export, which has each day's endpoints and
// outcomes as rows (content records the export).

// The most rows the page is answered (content's MaxDayRows); past it the
// answer says `truncated`.
export const MAX_DAY_ROWS = 20000

export const sysUsageRow = r => {
  const outcomes = r.outcomes || {}
  return {
    id: `${r.day}:${r.api_key_id}`,
    day: r.day || '',
    organizationID: r.organization_id,
    organization: r.organization || '',
    keyID: r.api_key_id,
    keyName: r.key_name || '',
    requests: Number(r.requests) || 0,
    ok: Number(outcomes['2xx']) || 0,
    clientError: Number(outcomes['4xx']) || 0,
    rateLimited: Number(outcomes['429']) || 0,
    serverError: Number(outcomes['5xx']) || 0
  }
}

export const sysUsagePage = body => ({
  rows: body && Array.isArray(body.items) ? body.items.filter(r => r && typeof r === 'object').map(sysUsageRow) : [],
  truncated: !!(body && body.truncated)
})

// A count column's cell and CSV value.
const count = field => ({ render: cell => (cell.data ? formatCount(cell.data[field]) : ''), exportValue: r => String(r[field]) })
const COUNTS = {
  requests: count('requests'),
  ok: count('ok'),
  clientError: count('clientError'),
  rateLimited: count('rateLimited'),
  serverError: count('serverError')
}

export const SysUsageTab = () => {
  const ids = { from: useId(), to: useId(), org: useId() }
  const today = dayOf(Date.now())
  const [from, setFrom] = useState(() => addDays(today, -29))
  const [to, setTo] = useState(today)
  const [org, setOrg] = useState('')
  const [rows, setRows] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [exportError, exportFailure, setExportError] = useErrorLine()
  const [exporting, setExporting] = useState(false)
  const tableRef = useRef()

  const badRange = !from || !to || to < from
  const range = useMemo(() => (badRange ? null : { from: dayWindow(from).from, to: dayWindow(to).to, org }), [badRange, from, to, org])

  useEffect(() => {
    if (!range) return undefined
    let live = true
    setRows(undefined)
    getSysUsage(range).then(
      body => { if (live) { setError(null); setRows(sysUsagePage(body)) } },
      err => { if (live) { setRows(null); setError(serverMessage(err, "Usage couldn't be loaded."), err) } }
    )
    return () => { live = false }
  }, [range, setError])

  const exportCsv = async () => {
    if (!range || exporting) return
    setExporting(true)
    setExportError(null)
    try {
      await downloadUsageCsv(range, `api-usage-${from}-to-${to}.csv`)
    } catch (err) {
      setExportError(serverMessage(err, "The export couldn't be made."), err)
    }
    setExporting(false)
  }

  const list = rows ? rows.rows : rows
  const gridList = useGridList(list)
  const total = Array.isArray(list) ? list.reduce((n, r) => n + r.requests, 0) : 0

  return (
    <>
      <div className="ak-filters">
        <div className="ak-filter">
          <label className="sw-label" htmlFor={ids.from}>From</label>
          <input id={ids.from} type="date" className="form-control" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="ak-filter">
          <label className="sw-label" htmlFor={ids.to}>To</label>
          <input id={ids.to} type="date" className="form-control" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} />
        </div>
        <div className="ak-filter">
          <OrgPicker id={ids.org} label="Organization" value={org} onChange={setOrg} allOption={{ value: '', label: 'Every organization' }} />
        </div>
        <div className="ak-filter">
          <button type="button" className="btn sw-btn" disabled={!range || exporting} onClick={exportCsv}><IconDownload className="me-1" />Export CSV</button>
        </div>
      </div>

      {badRange && <Notice tone="warn" role="alert">"To" must be on or after "From".</Notice>}
      {exportError && <Notice tone="bad" role="alert">{exportError}<ErrorReference error={exportFailure} /></Notice>}
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {!badRange && rows && rows.truncated &&
        <Notice tone="warn" role="status">{`This range has more rows than this page shows (the first ${formatCount(MAX_DAY_ROWS)}), so the totals are short. Narrow the range or pick an organization, or export the CSV (up to 100,000 rows).`}</Notice>}
      {!badRange && Array.isArray(list) && list.length > 0 &&
        <RollupTiles label="Totals" tiles={[{ key: 'total', label: 'Requests', value: formatCount(total) }]} />}
      {!badRange && Array.isArray(list) && list.length === 0 && <p className="sw-empty-state">No requests in this period.</p>}
      {!badRange && Array.isArray(list) && list.length > 0 &&
        <FleetGrid title="Usage" subtitle={`Each key's requests per UTC day; the range is your days in ${zoneName()}.`} tableRef={tableRef} exportName="api-usage-by-day">
          <TableAgClient ref={tableRef} apiList={gridList} sortCol="day" sortDesc
            emptyState={{ Icon: IconList, title: 'No requests', message: 'No key was used in this period.' }}>
            <ColumnText field="day" header="Day (UTC)" fit="content" sortable />
            <ColumnText field="organization" header="Organization" sortable />
            <ColumnText field="keyName" header="Key" sortable />
            <ColumnFormat field="requests" header="Requests" {...COUNTS.requests} sortKind="number" fit="content" sortable />
            <ColumnFormat field="ok" header="Succeeded" {...COUNTS.ok} sortKind="number" fit="content" sortable />
            <ColumnFormat field="clientError" header="Refused" {...COUNTS.clientError} sortKind="number" fit="content" sortable />
            <ColumnFormat field="rateLimited" header="Rate limited" {...COUNTS.rateLimited} sortKind="number" fit="content" sortable />
            <ColumnFormat field="serverError" header="Server errors" {...COUNTS.serverError} sortKind="number" fit="content" sortable />
          </TableAgClient>
        </FleetGrid>}
      <p className="sw-hint small mt-2 mb-0">{UNDER_COUNT_NOTE}</p>
    </>
  )
}
